'use client';

import {
  Fragment,
  type ComponentType,
  type KeyboardEventHandler,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { GreaterThanOrEqual, LessThan, NotEquals } from '@/components/icons';
import { Chip, type ChipPopup } from '@/components/ui/chip';
import { isNegated, type Criterion } from '@/domain/search-criteria';
import { formatParts, type Arg, type Locale, type Message, type MessageKey } from '@/i18n';
import { useT } from '@/lib/locale-context';
import { criterionChipLabel, type SuggestContext } from './filter-suggest';
import styles from './criterion-chip.module.css';

/**
 * A criterion worn as a chip, the one look every filtering surface shares
 * (the criteria bar and the palette, docs/SEARCH.md "The bar"). A chip that
 * names an attribute reads in three parts (Leon, 2026-10-19: label and value
 * were one grey sentence): the attribute faint, the operator, the value in
 * the text colour, and an item value led by the dot a list row leads with.
 * The operator is the catalog sentence's own words (`criteria.chip.is` and
 * its siblings), so a negated one ("≠", "<") wears the accent and the chip
 * its accent hairline. A chip with no attribute word (a tag, a title)
 * is one run of words with its "not" in the accent. Values join with a faint
 * "or"; the ✕ removes the chip in one click. The words come from
 * `criterionChipLabel`, so a filter reads the same on every surface. A chip
 * never grows past its container: the attribute gives way first, then the
 * value, each with an ellipsis.
 *
 * The chip is the kit `Chip` (docs/DESIGN_SYSTEM.md "Chip"): md, outline, the
 * ✕ its square at the end; a negated chip takes the accent tone, whose edge is
 * the accent hairline (the "≠" or the "not" stays the cue that is not colour).
 */

/** The keys a descriptor's template words wear a style on: the "not" of a
 *  negated chip or row in the accent, the "or" between values faint. */
const STYLED_WORDS: Partial<Record<MessageKey, string>> = {
  'criteria.chip.not': styles.not,
  'criteria.word.not': styles.not,
  'criteria.chip.or': styles.or,
};

/** The chip sentences that name an attribute: their template words are the
 *  operator (with the accent when it negates), `{attribute}` and `{value}`
 *  their two other parts. */
const FRAMES: Partial<Record<MessageKey, string>> = {
  'criteria.chip.is': styles.op,
  'criteria.chip.isNot': styles.opNot,
  'criteria.chip.levelAtLeast': styles.op,
  'criteria.chip.levelBelow': styles.opNot,
  'criteria.chip.contains': styles.op,
  'criteria.chip.containsNot': styles.opNot,
};

/** The operator signs drawn as icons (the UI face is a Latin subset, and a
 *  fallback font's sign varies by platform; icons.ts: no unicode glyphs). The
 *  catalog keeps the sign as text, so a locale that spells the operator in
 *  words gets its words, and a screen reader hears the sign. */
const SIGNS: Record<string, ComponentType<{ className?: string; 'aria-hidden'?: boolean }>> = {
  '≠': NotEquals,
  '≥': GreaterThanOrEqual,
  '<': LessThan,
};

/** An operator's template words, its sign (if it is one) drawn as an icon. */
function operator(text: string, style: string, key: number): ReactNode {
  const sign = Object.keys(SIGNS).find((s) => text.trim() === s);
  if (!sign) {
    return (
      <span key={key} className={style}>
        {text}
      </span>
    );
  }
  const Icon = SIGNS[sign]!;
  const [before, after] = text.split(sign) as [string, string];
  return (
    <span key={key} className={style}>
      {before}
      <Icon className={styles.sign} aria-hidden />
      <span className={styles.srOnly}>{sign}</span>
      {after}
    </span>
  );
}

/** What a rendering knows besides the message: the element keys' path, and
 *  whether the plain words under a value are item titles (each led by a dot). */
interface WordsOpts {
  path: string;
  items: boolean;
  inValue: boolean;
}

/** One plain word run under a value: an item title wears the dot. */
function leaf(text: string, opts: WordsOpts, key: string): ReactNode {
  if (!(opts.items && opts.inValue)) return <Fragment key={key}>{text}</Fragment>;
  return <CriterionItemTitle key={key} title={text} />;
}

/** An item value's title as the chip shows it, led by the dot a list row
 *  leads with; the chip editor's parent rows wear it too. */
export function CriterionItemTitle({ title }: { title: string }) {
  return (
    <span className={styles.item}>
      <span className={styles.dot} aria-hidden />
      {title}
    </span>
  );
}

/**
 * A chip's or a suggestion row's descriptor as elements, in one locale: the
 * template words of `STYLED_WORDS` and `FRAMES` keys in their style, every
 * other word as text. A plain-text argument of a styled key is passed as an
 * element, so the formatter keeps it apart from the template's own words.
 */
function words(locale: Locale, message: Message, opts: WordsOpts): ReactNode {
  const frame = FRAMES[message.key];
  const style = frame ?? STYLED_WORDS[message.key];
  const args: Record<string, Arg | ReactNode> = {};
  for (const [name, value] of Object.entries(message.args ?? {})) {
    const path = `${opts.path}.${name}`;
    const inValue = opts.inValue || (frame !== undefined && name === 'value');
    // A nested phrase renders on its own; a plain word run under a styled key
    // or a value is passed as an element (an item title wears its dot).
    let arg: Arg | ReactNode = value;
    if (typeof value === 'object' && !Array.isArray(value)) {
      arg = words(locale, value as Message, { ...opts, path, inValue });
    }
    if (typeof arg === 'string' && (style || inValue)) arg = leaf(arg, { ...opts, inValue }, path);
    if (frame && (name === 'attribute' || name === 'value')) {
      arg = (
        <span key={path} className={name === 'attribute' ? styles.attr : styles.value}>
          {arg as ReactNode}
        </span>
      );
    }
    args[name] = arg;
  }
  const parts = formatParts<ReactNode>(locale, message.key, args);
  if (!style && parts.every((p) => typeof p === 'string')) return parts.join('');
  return (
    <Fragment key={opts.path}>
      {parts.map((p, i) =>
        typeof p === 'string' && frame ? (
          operator(p, frame, i)
        ) : typeof p === 'string' && style ? (
          <span key={i} className={style}>
            {p}
          </span>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </Fragment>
  );
}

/** A criteria descriptor (a chip's label, a negated suggestion row's
 *  `display`) in the viewer's locale, with the operator words styled. */
export function CriterionWords({ message, items = false }: { message: Message; items?: boolean }) {
  const t = useT();
  return <>{words(t.locale, message, { path: 'w', items, inValue: false })}</>;
}

/** Does the chip wear the exclusion's accent hairline? A negated chip does;
 *  an empty chip ("Has a tag") reads as its own words, like a boolean. */
export function wearsExclusion(c: Criterion): boolean {
  return isNegated(c) && !('empty' in c && c.empty);
}

/** Is a chip's value an item (its title, led by a dot)? */
function itemValued(c: Criterion): boolean {
  return (c.field === 'parent' || c.field === 'ancestor') && !('empty' in c && c.empty);
}

/** The chip's words as a button (`Chip`'s button mode): the criteria bar's
 *  menu trigger on an attribute chip, a title chip's way back into the text.
 *  The ref, `tabIndex` and `onKeyDown` land on the words, beside the ✕ (the
 *  bar's arrow-key walk). */
export interface CriterionChipAct {
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  popup?: ChipPopup;
  bodyRef?: Ref<HTMLButtonElement>;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  tabIndex?: number;
}

export function CriterionChip({
  criterion,
  suggestCtx,
  onRemove,
  act,
  title,
  shrink = true,
  testId,
}: {
  criterion: Criterion;
  suggestCtx: SuggestContext;
  onRemove: () => void;
  /** The words as a button; static words when omitted (the palette). */
  act?: CriterionChipAct;
  /** The words' tooltip (a title chip's "edit the term"); the kit puts it on
   *  the body, never over the ✕. */
  title?: string;
  /** The chip gives way in its row, the words ending in an ellipsis (the
   *  default); `false` keeps it whole (the palette's input row). */
  shrink?: boolean;
  testId?: string;
}) {
  const t = useT();
  const label = criterionChipLabel(criterion, suggestCtx);
  const negated = wearsExclusion(criterion);
  const words = (
    <span className={label.key in FRAMES ? styles.frame : styles.text}>
      <CriterionWords message={label} items={itemValued(criterion)} />
    </span>
  );
  const chip = {
    size: 'md',
    look: 'outline',
    tone: negated ? 'accent' : 'neutral',
    shrink,
    title,
    testId,
    'data-negated': negated || undefined,
    onRemove,
    removeLabel: t('criteria.bar.removeFilter', { label }),
  } as const;
  // The chip's sentence as one name on the button: its parts are separate
  // boxes, which a computed name would space apart.
  return act ? (
    <Chip {...chip} {...act} ariaLabel={t.msg(label)}>
      {words}
    </Chip>
  ) : (
    <Chip {...chip}>{words}</Chip>
  );
}
